import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Autocomplete, AutocompleteProps } from "@airhelp/react";

import { airlineApi } from "api";
import useDebouncedValue from "hooks/useDebouncedValue";
import { Airline } from "types/airline";

const AirlineAutocomplete: React.FC<AirlineAutocompleteProps> = ({
  errorMessage,
  defaultAirline,
  placeholder,
  ...props
}) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounced = useDebouncedValue(searchTerm, 300);
  const [isLoading, setIsLoading] = useState(false);
  const [airlinesOptions, setAirlinesOptions] = useState<AirlineOption[]>([]);

  const getAirlineResponse = async (searchDebounced: string) => {
    const [error, response] = await airlineApi.fetchAirlines(searchDebounced);

    if (error) {
      throw error;
    } else {
      return response.airlines as Airline[];
    }
  };

  useEffect(() => {
    if (!searchDebounced || searchDebounced.length < 2) {
      return;
    }

    setIsLoading(true);

    getAirlineResponse(searchTerm)
      .then((airlines) =>
        airlines.map((airline) => {
          return { label: airline.displayName, value: airline };
        })
      )
      .then((airlines) => airlines && setAirlinesOptions(airlines))
      .finally(() => setIsLoading(false));
  }, [searchDebounced, setIsLoading, setAirlinesOptions]);

  return (
    <Autocomplete
      {...props}
      placeholder={placeholder || t("funnels.airline_placeholder")}
      options={airlinesOptions}
      isLoading={isLoading}
      onInputChange={setSearchTerm}
      errorMessage={errorMessage}
      defaultValue={defaultAirline ? { label: defaultAirline.displayName, value: defaultAirline } : null}
    />
  );
};

type AirlineOption = {
  label: string;
  value: Airline;
  errorMessage?: string;
};

type AirlineAutocompleteProps = AutocompleteProps<AirlineOption> & {
  defaultAirline?: Airline;
  placeholder?: string;
};

export default AirlineAutocomplete;
